import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api/axiosConfig';
import AuthContext from '../contexts/AuthContext';
import { toLocalDateString } from '../utils/localDate';

const OVERSEER_ROLES = ['OWNER', 'SUPER_ADMIN', 'INTERMEDIATE_ADMIN'];

/** 마감이 오늘 안인가. 주 단위 계획은 주말까지 여유가 있어 여기서 갈린다. */
const dueToday = (dueAt) =>
  toLocalDateString(new Date(dueAt)) === toLocalDateString(new Date());

const timeText = (iso) => {
  const d = new Date(iso);
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h < 12 ? '오전' : '오후'} ${h % 12 || 12}:${m}`;
};

const dayText = (iso) => {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

/** 들를 곳 하나. 구역이 없으면 건물 전체를 한 장으로 훑는 곳이다. */
const stopName = (stop) =>
  stop.section?.name || `${stop.facility?.name ?? ''} 전체`;

/**
 * 근무자가 여는 첫 화면.
 *
 * 예전에는 담당 시설을 열고 구역을 늘어놓아, 오늘 무엇을 해야 하는지는 근무자가
 * 기억해서 골라야 했다. 이제 그 답은 계획의 회차에 있으므로 그대로 보여준다.
 *
 * 서버가 '나에게 할당된 계획 + 아무에게도 할당되지 않은 계획' 만 낸다. 뒤엣것이
 * 빠지면 아무도 안 맡은 일이 아무에게도 안 보여 조용히 밀린다.
 */
const TodayPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const oversees = OVERSEER_ROLES.includes(user?.role);

  const [rounds, setRounds] = useState([]);
  // 못 한 채로 지나간 회차. 뒤늦게 하러 갈 곳이다.
  const [missed, setMissed] = useState([]);
  const [loading, setLoading] = useState(true);
  // 관리자는 기본으로 전부 본다. 무엇이 밀리는지 보는 것이 그들의 일이다.
  const [mineOnly, setMineOnly] = useState(false);
  // 펼쳐 둔 회차. 기본은 접힘 -- 열세 줄이 펼쳐진 채로 나오면 훑을 수가 없다.
  const [open, setOpen] = useState({});

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = oversees && mineOnly ? { mine: '1' } : {};
      const [outstanding, missedRes] = await Promise.all([
        api.get('/inspection-plans/slots/outstanding', { params }),
        api.get('/inspection-plans/slots/missed', { params }),
      ]);
      setRounds(outstanding.data || []);
      setMissed(missedRes.data || []);
    } catch (err) {
      console.error('Failed to load outstanding inspections', err);
    } finally {
      setLoading(false);
    }
  }, [oversees, mineOnly]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const today = rounds.filter((r) => dueToday(r.dueAt));
  const later = rounds.filter((r) => !dueToday(r.dueAt));

  const periodText = (round) => {
    const from = new Date(round.periodStart);
    const to = new Date(round.periodEnd);
    return `${from.getMonth() + 1}/${from.getDate()}~${to.getMonth() + 1}/${to.getDate()}`;
  };

  const startAt = (stop) =>
    navigate(`/facilities/${stop.facility.id}/inspect?stopId=${stop.stopId}`);

  const renderRound = (round, urgent) => {
    const remaining = round.remaining;
    const many = remaining.length > 1;
    const expanded = open[round.id];
    // 회차 단위 '점검' 은 남은 첫 곳에서 시작한다. 제출하고 나면 다음 곳을
    // 이어서 할지 물으므로, 한 번 눌러 끝까지 돈다.
    const first = remaining[0];

    return (
      <div key={round.id} className="bg-white rounded-4 border p-3 mb-2">
        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
          <span className="fw-bold">{round.planName}</span>
          <span className="badge bg-light text-dark border fw-normal">
            {round.labelText}
          </span>
          <span
            className={`ms-auto text-nowrap ${urgent ? 'text-danger' : 'text-muted'}`}
            style={{ fontSize: '0.78rem' }}
          >
            {/* 지난 회차에는 기한이 없다. 언제 것인지가 알고 싶은 값이다. */}
            {round.dueAt
              ? urgent
                ? `${timeText(round.dueAt)} 마감`
                : `${dayText(round.dueAt)}까지`
              : periodText(round)}
          </span>
        </div>

        <div className="d-flex align-items-center gap-2">
          <div className="text-muted flex-grow-1" style={{ fontSize: '0.75rem' }}>
            {round.progress?.total > 1
              ? `${round.route?.name ? `${round.route.name} · ` : ''}${round.progress.done}/${round.progress.total}곳`
              : remaining.length === 1
                ? stopName(remaining[0])
                : ''}
          </div>

          {first?.facility?.id && (
            <button
              className="btn btn-sm btn-primary rounded-pill px-3 text-nowrap"
              onClick={() => startAt(first)}
            >
              점검
            </button>
          )}

          {/* 여러 곳을 도는 회차만 펼친다. 일부를 놓쳤을 때 그 곳부터 이어가려면
              골라야 한다. */}
          {many && (
            <button
              className="btn btn-sm btn-white border rounded-pill px-2"
              onClick={() =>
                setOpen((prev) => ({ ...prev, [round.id]: !prev[round.id] }))
              }
            >
              <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`}></i>
            </button>
          )}
        </div>

        {/* 공유 방식은 어느 경로를 돌지 실제로 돌 때 정해지므로, 그때까지 들를
            곳이 없다. 숨기면 할 일이 조용히 사라지므로 그렇다고 말해 둔다. */}
        {remaining.length === 0 && (
          <div className="text-muted small border-top pt-2 mt-2">
            들를 곳이 아직 정해지지 않았습니다.
          </div>
        )}

        {many && expanded && (
          <div className="mt-2">
            {remaining.map((stop) => (
              <div
                key={stop.id}
                className="d-flex align-items-center gap-2 py-2 border-top"
                style={{ minHeight: '52px' }}
              >
                <i className="bi bi-circle text-muted" style={{ fontSize: '0.7rem' }}></i>
                <span className="text-truncate">{stopName(stop)}</span>
                {stop.facility?.id && (
                  <button
                    className="btn btn-sm btn-white border rounded-pill px-3 ms-auto text-nowrap"
                    onClick={() => startAt(stop)}
                  >
                    점검
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="pb-5">
      {/* 묶음 제목이 그 아래 계획 이름보다 작고 흐려서, 제목이 아니라 곁글처럼
          읽혔다. 아래 내용보다는 커야 묶음이 갈린다. */}
      <style>{`
        .today-group {
          font-size: 1.05rem;
          font-weight: 700;
          color: #374151;
          margin-bottom: 0.5rem;
        }
      `}</style>
      <div className="d-flex justify-content-between align-items-center gap-2 mb-3 flex-wrap">
        <div>
          <h4 className="fw-bold mb-1">오늘 할 점검</h4>
          <p className="text-muted small mb-0">아직 하지 않은 것만 나옵니다.</p>
        </div>
        {/* 관리자만. 근무자는 어차피 자기 것만 나오므로 고를 것이 없다. */}
        {oversees && (
          <div className="btn-group btn-group-sm" role="group">
            <button
              className={`btn ${mineOnly ? 'btn-white border' : 'btn-primary'}`}
              onClick={() => setMineOnly(false)}
            >
              전체
            </button>
            <button
              className={`btn ${mineOnly ? 'btn-primary' : 'btn-white border'}`}
              onClick={() => setMineOnly(true)}
            >
              내 것만
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      ) : rounds.length === 0 && missed.length === 0 ? (
        <div className="bg-white rounded-4 border text-center p-5">
          <i className="bi bi-check2-circle display-6 d-block mb-3 text-success opacity-50"></i>
          <h6 className="fw-bold mb-1">남은 점검이 없습니다</h6>
          <p className="text-muted small mb-3">
            이번 주에 해야 할 것을 모두 마쳤습니다.
          </p>
          <Link to="/inspection-matrix" className="small fw-bold text-decoration-none">
            주간 점검 현황 보기
            <i className="bi bi-chevron-right ms-1" style={{ fontSize: '0.7rem' }}></i>
          </Link>
        </div>
      ) : (
        <>
          {today.length > 0 && (
            <>
              <div className="today-group">오늘 마감</div>
              {today.map((r) => renderRound(r, true))}
            </>
          )}

          {later.length > 0 && (
            <>
              {/* 주 단위 계획은 어느 날 할지 사람이 고른다. 오늘 것과 섞어 두면
                  전부 오늘 해야 하는 것처럼 보여 쫓기게 된다. */}
              <div className="today-group mt-4">이번 주 안에</div>
              {later.map((r) => renderRound(r, false))}
            </>
          )}

          {missed.length > 0 && (
            <>
              {/* 월요일 순찰을 못 해 토요일에 도는 일이 있는데, 시작할 길이 아예
                  없어서 제출하지 못한 적이 있었다. 여기서 바로 갈 수 있게 둔다.
                  지난주 것은 나오지 않는다 -- 그건 만회가 아니라 장부 맞추기다. */}
              <div className="today-group mt-4">이번 주에 못 한 점검</div>
              {missed.map((r) => renderRound(r, false))}
              <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                지금 해도 그 회차는 미이행 그대로입니다. 다녀온 곳만 표시됩니다.
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default TodayPage;
